import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { RefreshCcw } from "lucide-react";
import { LegalFooter } from "./privacy-policy";

const BASE = import.meta.env.BASE_URL.replace(/\/$/, "");
const LAST_UPDATED = "26 September 2026";

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-background">
      <header className="border-b sticky top-0 z-50 bg-background/95 backdrop-blur">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <Link href={`${BASE}/`}>
            <span className="font-bold text-lg text-primary cursor-pointer">RetailPulse</span>
          </Link>
          <div className="flex items-center gap-3">
            <Link href={`${BASE}/login`}><Button variant="ghost" size="sm">Sign in</Button></Link>
            <Link href={`${BASE}/signup`}><Button size="sm">Get started free</Button></Link>
          </div>
        </div>
      </header>

      <section className="border-b bg-muted/30">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/10">
            <RefreshCcw className="w-3 h-3 mr-1" /> Legal
          </Badge>
          <h1 className="text-4xl font-bold tracking-tight mb-3">Refund Policy</h1>
          <p className="text-muted-foreground">Last updated: {LAST_UPDATED}</p>
        </div>
      </section>

      <main className="max-w-3xl mx-auto px-4 sm:px-6 py-14">
        <div className="space-y-10 text-sm leading-relaxed text-foreground">

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">1. Our commitment</h2>
            <p className="text-muted-foreground">
              We want you to love RetailPulse. If something goes wrong on our end — a billing error, duplicate charge, or a service failure that prevents you from using the platform — we will make it right. This policy explains when and how we issue refunds.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">2. Free trial</h2>
            <p className="text-muted-foreground">
              All new accounts receive a <strong className="text-foreground">10-day free trial</strong>. No payment is required during the trial, so no refund applies for the trial period. You can cancel at any time before the trial ends and you will never be charged.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">3. When you are eligible for a refund</h2>
            <p className="text-muted-foreground">You may request a refund in the following situations:</p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">Duplicate charge:</strong> You were charged more than once for the same billing period due to a payment processing error.
              </li>
              <li>
                <strong className="text-foreground">Billing error:</strong> You were charged an incorrect amount or charged after a valid cancellation.
              </li>
              <li>
                <strong className="text-foreground">Service unavailability:</strong> RetailPulse was inaccessible for more than 72 consecutive hours in a billing period due to an issue on our side (excluding scheduled maintenance).
              </li>
              <li>
                <strong className="text-foreground">First subscription charge — 48-hour window:</strong> If you subscribed for the first time and realise RetailPulse is not right for your business, you may request a full refund within <strong className="text-foreground">48 hours</strong> of the first charge — provided you have not processed more than 10 sales transactions since subscribing.
              </li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">4. When refunds are not issued</h2>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>Renewal charges for subscriptions that were not cancelled before the renewal date.</li>
              <li>Partial months — subscriptions are monthly and non-prorated.</li>
              <li>Accounts suspended or terminated for violating our <Link href={`${BASE}/terms-of-service`}><span className="text-primary underline cursor-pointer">Terms of Service</span></Link>.</li>
              <li>Requests made more than 14 days after the charge date (except for documented billing errors).</li>
              <li>Change-of-mind cancellations beyond the 48-hour first-charge window.</li>
            </ul>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">5. How to request a refund</h2>
            <p className="text-muted-foreground">
              Contact us with your refund request via:
            </p>
            <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
              <li>
                <strong className="text-foreground">WhatsApp:</strong>{" "}
                <a href="https://wa.me/2349040460813?text=Hi%2C%20I'd%20like%20to%20request%20a%20refund" target="_blank" rel="noopener noreferrer" className="text-primary underline">+234 904 046 0813</a>
              </li>
              <li>
                <strong className="text-foreground">Email:</strong>{" "}
                <a href="mailto:support@retailpulsepos.com?subject=Refund%20Request" className="text-primary underline">support@retailpulsepos.com</a>
              </li>
            </ul>
            <p className="text-muted-foreground">
              Please include: your registered email address, your store name, the charge date, the amount charged, and a brief reason for the request. We will review and respond within <strong className="text-foreground">3 business days</strong>.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">6. Refund processing</h2>
            <p className="text-muted-foreground">
              Approved refunds are processed via Paystack back to your original payment method. Refunds typically appear within <strong className="text-foreground">5–10 business days</strong>, depending on your bank. We will notify you by WhatsApp or email once the refund has been initiated.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">7. Cancellation</h2>
            <p className="text-muted-foreground">
              To cancel your subscription, email{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a> or message us. Cancellation stops future charges and takes effect at the end of the current paid period — your access continues until then. You can request and receive an export of your business data from support within 30 days after cancellation takes effect; after that period we delete your account and business data, except records required by law. Cancellation does not automatically trigger a refund unless it falls within an eligible window above.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">8. Changes to this policy</h2>
            <p className="text-muted-foreground">
              We may update this Refund Policy from time to time. Changes will be communicated via email or in-app notice. The current version always governs active subscriptions from the date it takes effect.
            </p>
          </section>

          <section className="space-y-3">
            <h2 className="text-xl font-semibold">9. Contact us</h2>
            <p className="text-muted-foreground">
              Still have questions?{" "}
              <a href="https://wa.me/2349040460813" target="_blank" rel="noopener noreferrer" className="text-primary underline">WhatsApp us</a>
              {" "}or email{" "}
              <a href="mailto:support@retailpulsepos.com" className="text-primary underline">support@retailpulsepos.com</a>. We typically respond within 24 hours on business days.
            </p>
          </section>

        </div>
      </main>

      <LegalFooter />
    </div>
  );
}
